import { EXPOSED_STATUSES, formatMoney, STATUS_META, type DealStatus } from '@accel/shared';
import {
  seriesColour,
  SERIES_REST,
  type BarRow,
  type FunnelStage,
  type Slice,
  type TrendPoint,
} from '../components/charts';
import type { DealSummary } from '../lib/types';

/**
 * Everything the dashboards draw, derived from the deal list the API already
 * returns for this session.
 *
 * Two rules run through all of it.
 *
 * **Currencies never mix.** There is no FX rate in the platform, so adding a
 * euro to a dollar would invent one. Every money figure is scoped to a single
 * currency, chosen by the reader; anything that spans the book is a count.
 *
 * **Amounts stay exact until the last step.** Sums are `bigint` minor units
 * and are formatted from that. The `number` on each chart point exists only
 * to give a bar its width — it is never what the reader is shown.
 */

const CLOSED: DealStatus[] = ['SETTLED', 'WRITTEN_OFF', 'CANCELLED', 'REJECTED'];

const minor = (value: string | null): bigint => (value ? BigInt(value) : 0n);

/** Minor units to a plain number, for geometry only. */
const forGeometry = (value: bigint): number => Number(value) / 100;

/**
 * The currencies this book actually trades in, busiest first.
 *
 * Ordered by live deals rather than by total value: the currency somebody
 * works in every day should be the one the page opens on, even when a single
 * large deal sits in another.
 */
export function currenciesInPlay(deals: DealSummary[]): string[] {
  const live = new Map<string, number>();
  const all = new Map<string, number>();

  for (const deal of deals) {
    all.set(deal.currency, (all.get(deal.currency) ?? 0) + 1);
    if (!CLOSED.includes(deal.status)) live.set(deal.currency, (live.get(deal.currency) ?? 0) + 1);
  }

  return [...all.keys()].sort(
    (a, b) => (live.get(b) ?? 0) - (live.get(a) ?? 0) || (all.get(b) ?? 0) - (all.get(a) ?? 0),
  );
}

export interface Totals {
  face: bigint;
  advanced: bigint;
  fees: bigint;
  exposure: bigint;
  settled: bigint;
  currency: string;
}

export function totals(deals: DealSummary[], currency: string): Totals {
  const scoped = deals.filter((d) => d.currency === currency);
  let face = 0n;
  let advanced = 0n;
  let fees = 0n;
  let exposure = 0n;
  let settled = 0n;

  for (const deal of scoped) {
    face += minor(deal.faceMinor);
    if (deal.disbursedAt) {
      advanced += minor(deal.advanceMinor);
      fees += minor(deal.feeMinor);
    }
    if (EXPOSED_STATUSES.includes(deal.status)) exposure += minor(deal.advanceMinor);
    if (deal.status === 'SETTLED') settled += minor(deal.faceMinor);
  }

  return { face, advanced, fees, exposure, settled, currency };
}

/**
 * What was advanced each month, for the last `months` months.
 *
 * Reads the disbursement date rather than the created date: the question a
 * supplier is asking is when cash arrived, not when paperwork did.
 */
export function monthlyAdvances(
  deals: DealSummary[],
  currency: string,
  months = 6,
): TrendPoint[] {
  const buckets = new Map<string, bigint>();
  const now = new Date();

  for (let i = months - 1; i >= 0; i--) {
    const when = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    buckets.set(when.toISOString().slice(0, 7), 0n);
  }

  for (const deal of deals) {
    if (deal.currency !== currency || !deal.disbursedAt) continue;
    const key = deal.disbursedAt.slice(0, 7);
    if (!buckets.has(key)) continue;
    buckets.set(key, (buckets.get(key) ?? 0n) + minor(deal.advanceMinor));
  }

  return [...buckets.entries()].map(([key, value]) => ({
    label: new Date(`${key}-01T00:00:00Z`).toLocaleDateString('en-GB', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    }),
    value: forGeometry(value),
    display: formatMoney(value, currency),
  }));
}

/** Financed value grouped by the counterparty on the other side of the deal. */
export function byCounterparty(
  deals: DealSummary[],
  side: 'sme' | 'payer',
  currency: string,
): BarRow[] {
  const groups = new Map<string, { name: string; total: bigint; count: number }>();

  for (const deal of deals) {
    if (deal.currency !== currency || CLOSED.includes(deal.status)) continue;
    const party = deal[side];
    const entry = groups.get(party.id) ?? { name: party.name, total: 0n, count: 0 };
    entry.total += minor(deal.faceMinor);
    entry.count += 1;
    groups.set(party.id, entry);
  }

  return [...groups.entries()]
    .sort((a, b) => (b[1].total > a[1].total ? 1 : -1))
    .map(([id, entry]) => ({
      key: id,
      name: entry.name,
      value: forGeometry(entry.total),
      display: formatMoney(entry.total, currency, { compact: true }),
      meta: `${entry.count} live deal${entry.count === 1 ? '' : 's'}`,
    }));
}

/**
 * Concentration of funded exposure, capped at five named slices.
 *
 * Past five the segments stop being comparable and the legend stops being
 * readable, so the tail folds into one rest-of-book slice rather than being
 * given a sixth colour.
 */
export function exposureSlices(
  deals: DealSummary[],
  side: 'sme' | 'payer',
  currency: string,
): Slice[] {
  const groups = new Map<string, { name: string; total: bigint }>();

  for (const deal of deals) {
    if (deal.currency !== currency || !EXPOSED_STATUSES.includes(deal.status)) continue;
    const party = deal[side];
    const entry = groups.get(party.id) ?? { name: party.name, total: 0n };
    entry.total += minor(deal.advanceMinor);
    groups.set(party.id, entry);
  }

  const ordered = [...groups.entries()].sort((a, b) => (b[1].total > a[1].total ? 1 : -1));
  const named = ordered.slice(0, 5);
  const rest = ordered.slice(5);

  const slices: Slice[] = named.map(([id, entry], index) => ({
    key: id,
    name: entry.name,
    value: forGeometry(entry.total),
    display: formatMoney(entry.total, currency, { compact: true }),
    colour: seriesColour(index),
  }));

  if (rest.length > 0) {
    const total = rest.reduce((sum, [, entry]) => sum + entry.total, 0n);
    slices.push({
      key: 'rest',
      name: `${rest.length} others`,
      value: forGeometry(total),
      display: formatMoney(total, currency, { compact: true }),
      colour: SERIES_REST,
    });
  }

  return slices;
}

/**
 * How many deals reached each stage of the rail.
 *
 * Counted by the furthest stage a deal has got to, so a settled deal counts
 * at every stage behind it — which is what makes the shape a funnel rather
 * than a snapshot of where everything is sitting right now.
 */
export function funnelStages(deals: DealSummary[]): FunnelStage[] {
  const gates: { key: string; name: string; stage: number }[] = [
    { key: 'submitted', name: 'Submitted', stage: 0 },
    { key: 'validated', name: 'Validated', stage: 1 },
    { key: 'confirmed', name: 'Payer confirmed', stage: 3 },
    { key: 'financed', name: 'Financed', stage: 5 },
    { key: 'settled', name: 'Settled', stage: 7 },
  ];

  return gates.map((gate) => ({
    key: gate.key,
    name: gate.name,
    count: deals.filter((deal) => {
      const meta = STATUS_META[deal.status];
      // An off-ramp says the deal left the rail; it still reached the stages
      // behind it, but it never reached this one if it stopped earlier.
      if (deal.status === 'SETTLED') return true;
      return meta.stage >= gate.stage && !(meta.offRamp && meta.stage === gate.stage);
    }).length,
  }));
}

/** Deals that went through with no human intervention and no trouble. */
export function straightThrough(deals: DealSummary[]): { percent: number; exceptions: number } {
  if (deals.length === 0) return { percent: 100, exceptions: 0 };
  const exceptions = deals.filter((d) =>
    ['REFERRED', 'DISPUTED', 'OVERDUE', 'RECOURSE', 'WRITTEN_OFF', 'REJECTED'].includes(d.status),
  ).length;
  return {
    percent: ((deals.length - exceptions) / deals.length) * 100,
    exceptions,
  };
}

/** Share of settled deals that were paid on or before the due date. */
export function settlementDiscipline(deals: DealSummary[]): { percent: number; settled: number } {
  const settled = deals.filter((d) => d.status === 'SETTLED' && d.settledAt);
  if (settled.length === 0) return { percent: 0, settled: 0 };
  const onTime = settled.filter((d) => d.settledAt!.slice(0, 10) <= d.dueDate.slice(0, 10)).length;
  return { percent: (onTime / settled.length) * 100, settled: settled.length };
}

/** How much of the confirmed book has actually been turned into cash. */
export function advanceShare(
  deals: DealSummary[],
  currency: string,
): { percent: number; advanced: bigint; base: bigint } {
  let advanced = 0n;
  let base = 0n;

  for (const deal of deals) {
    if (deal.currency !== currency || CLOSED.includes(deal.status)) continue;
    base += minor(deal.financeableMinor ?? deal.confirmedMinor ?? deal.faceMinor);
    if (deal.disbursedAt) advanced += minor(deal.advanceMinor);
  }

  return { percent: base > 0n ? Number((advanced * 10000n) / base) / 100 : 0, advanced, base };
}
